import { Box, Button, Field, Flex, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import type { BriefTemplateContent } from '@fluenceur/types';
import { Plus, Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { QuantityStepper } from '@/pages/collabs/brief-templates/editor/controls/QuantityStepper';
import {
  PLATFORM_LABEL,
  deliverableLabel,
  deliverableSpec,
} from '@/pages/collabs/brief-templates/_shared/brief-summary-lines';
import {
  DELIVERABLE_PRESETS,
  deliverableFromPreset,
} from '@/pages/collabs/brief-templates/editor/sections/deliverable-presets';

interface CollabTermsDeliverablesProps {
  content: BriefTemplateContent;
  updateContent: (updater: (prev: BriefTemplateContent) => BriefTemplateContent) => void;
  /**
   * Blank briefs may compose their deliverables here (add/remove) — the draft
   * step only covers the common sections. Template briefs keep the design rule:
   * the composition comes from the brief, only quantities adjust.
   */
  allowCompose: boolean;
}

/** Per-recipient deliverable quantities (wizard terms step). */
export const CollabTermsDeliverables = ({
  content,
  updateContent,
  allowCompose,
}: CollabTermsDeliverablesProps) => {
  const { t } = useTranslation();
  const { deliverables } = content;

  const setQuantity = (id: string, quantity: number) =>
    updateContent((prev) => ({
      ...prev,
      deliverables: prev.deliverables.map((d) => (d.id === id ? { ...d, quantity } : d)),
    }));

  const remove = (id: string) =>
    updateContent((prev) => ({
      ...prev,
      deliverables: prev.deliverables.filter((d) => d.id !== id),
    }));

  const add = (presetKey: string) => {
    const preset = DELIVERABLE_PRESETS.find((p) => p.key === presetKey);
    if (!preset) return;
    updateContent((prev) => ({
      ...prev,
      deliverables: [...prev.deliverables, deliverableFromPreset(preset)],
    }));
  };

  return (
    <Field.Root>
      <Field.Label fontFamily="heading" fontSize="12.5px" fontWeight="semibold">
        {t('invitations.modal.collab.wizard.terms.deliverablesLabel')}
      </Field.Label>
      <VStack align="stretch" gap="2" w="full">
        {deliverables.map((deliverable) => (
          <HStack
            key={deliverable.id}
            gap="3"
            borderWidth="1px"
            borderColor="cardBorder"
            rounded="lg"
            bg="panelBg"
            px="12px"
            py="2"
          >
            <QuantityStepper
              variableStyle
              value={deliverable.quantity}
              onChange={(value) => setQuantity(deliverable.id, value)}
              format={(v) => `${v} ×`}
              aria-label={t('collabs.briefTemplates.sections.deliverables.quantityLabel')}
            />
            <Box flex="1" minW="0">
              <Text fontFamily="heading" fontSize="12.5px" fontWeight="semibold" color="dark">
                {deliverableLabel(deliverable, t)}
              </Text>
              <Text fontSize="11.5px" color="fg.muted">
                {deliverableSpec(deliverable, t)}
              </Text>
            </Box>
            {allowCompose && (
              <IconButton
                aria-label={t('collabs.briefTemplates.sections.deliverables.remove')}
                variant="ghost"
                size="xs"
                onClick={() => remove(deliverable.id)}
              >
                <Xmark width={14} height={14} />
              </IconButton>
            )}
          </HStack>
        ))}

        {allowCompose && (
          <Flex>
            <MenuRoot positioning={{ placement: 'bottom-start' }}>
              <MenuTrigger asChild>
                <Button size="xs" variant="outline">
                  <Plus width={14} height={14} />
                  {t('collabs.briefTemplates.sections.deliverables.add')}
                </Button>
              </MenuTrigger>
              {/* Render inline (no body portal): this menu lives inside the send
                  modal's Dialog. A body-portaled content sits outside the dialog's
                  focus trap, which swallows the item click (menu closes, onSelect
                  never fires) and kills keyboard nav. Mirrors TemplatePicker's
                  "select within dialog" fix. */}
              <MenuContent minW="230px" portalled={false}>
                {DELIVERABLE_PRESETS.map((preset) => (
                  <MenuItem key={preset.key} value={preset.key} onSelect={() => add(preset.key)}>
                    <HStack justify="space-between" w="full">
                      <Text fontWeight="semibold">
                        {t(`collabs.briefTemplates.sections.deliverables.formats.${preset.key}`)}
                      </Text>
                      <Text fontSize="11.5px" color="fg.muted">
                        {PLATFORM_LABEL[preset.platform]}
                      </Text>
                    </HStack>
                  </MenuItem>
                ))}
              </MenuContent>
            </MenuRoot>
          </Flex>
        )}
      </VStack>
    </Field.Root>
  );
};
